import classes from './ImportFiles.module.scss';
import { ImportFile } from '@/models/models';
import {
  API_GET_IMPORT_FILE_HIGHLIGHTS_ENDPOINT,
  API_GET_IMPORT_FILES_ENDPOINT,
  createApiGetImportFileEndpoint,
} from '@/api/config';

import {
  Table,
  Text,
  Alert,
  ScrollArea,
  Pagination,
  ActionIcon,
  Button,
  Group,
  Modal,
  NativeSelect,
  Select,
  Stack,
  Tooltip,
} from '@mantine/core';
import {
  DatePickerInput,
  type DatesRangeValue,
  type DateStringValue,
} from '@mantine/dates';

import { TableSkeletonRows } from '@/components/LoadingSkeleton/LoadingSkeleton';
import PageHeader from '@/components/PageHeader/PageHeader';

import IconAlertCircle from '@tabler/icons-react/IconAlertCircle';
import IconArrowUp from '@tabler/icons-react/IconArrowUp';
import IconArrowDown from '@tabler/icons-react/IconArrowDown';
import IconDownload from '@tabler/icons-react/IconDownload';
import IconInfoCircle from '@tabler/icons-react/IconInfoCircle';
import IconRefresh from '@tabler/icons-react/IconRefresh';
import IconCalendar from '@tabler/icons-react/IconCalendar';

import apiClient from '@/api/client';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  DATE_PICKER_VALUE_FORMAT,
  formatDisplayDateTime,
} from '@/utils/dateFormat';
import { dateRangeQuery } from '@/utils/dateRangeFilters';
import { formatLocalizedNumber } from '@/utils/numberFormat';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

type SortDirection = 'asc' | 'desc';
type SortableColumn = keyof Pick<
  ImportFile,
  | 'id'
  | 'file_name'
  | 'source'
  | 'uploaded_at'
  | 'status'
  | 'total_records_count'
>;

interface SortConfig {
  column: SortableColumn;
  direction: SortDirection;
}

interface ImportFilesResponse {
  data: ImportFile[];
  total: number;
}

interface ImportFileHighlightsResponse {
  data: Array<{ uploaded_at: string | null }>;
}

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (
      error as {
        response?: {
          data?: { message?: unknown };
        };
      }
    ).response;

    if (typeof response?.data?.message === 'string') {
      return response.data.message;
    }
  }

  return error instanceof Error && error.message ? error.message : fallback;
}

export default function ImportFilesTable() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  // State for table data and loading
  const [data, setData] = useState<ImportFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'id',
    direction: 'desc',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [diagnosticFile, setDiagnosticFile] = useState<ImportFile | null>(null);
  const [uploadedRange, setUploadedRange] = useState<DatesRangeValue>([null, null]);
  const [selectedSource, setSelectedSource] = useState<ImportFile['source'] | null>(null);
  const [uploadedDateKeys, setUploadedDateKeys] = useState<Set<string>>(new Set());
  const [dateHighlightsLoading, setDateHighlightsLoading] = useState(false);
  const sourceOptions = useMemo(() => [
    { value: 'desktop_app', label: t('imports.sourceDesktopApp') },
    { value: 'web_app', label: t('imports.sourceWebApp') },
    { value: 'direct_api', label: t('imports.sourceDirectApi') },
    { value: 'sharepoint', label: t('imports.sourceSharePoint') },
    { value: 'api_rerun', label: t('imports.sourceApiRerun') },
    { value: 'legacy_unknown', label: t('imports.sourceLegacyUnknown') },
  ], [t]);
  const hasActiveFilters = Boolean(
    uploadedRange[0] || uploadedRange[1] || selectedSource
  );

  const handleSort = useCallback((column: SortableColumn) => {
    setSortConfig((prev) => {
      const newDirection = prev.column === column 
        ? (prev.direction === 'asc' ? 'desc' : 'asc')
        : 'asc';
      
      return {
        column,
        direction: newDirection,
      };
    });
    
    // Reset to first page when sorting changes
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const fetchTableData = async () => {
      try {
        setLoading(true);
        const [uploadedFrom, uploadedTo] = dateRangeQuery(uploadedRange);
        const response = await apiClient.get<ImportFilesResponse>(
          API_GET_IMPORT_FILES_ENDPOINT,
          {
            params: {
              limit: itemsPerPage,
              offset: (currentPage - 1) * itemsPerPage,
              uploaded_from: uploadedFrom,
              uploaded_to: uploadedTo,
              source: selectedSource || undefined,
              sort_by: sortConfig.column,
              sort_order: sortConfig.direction,
            },
            headers: {
              Accept: 'application/json',
              'Access-Control-Allow-Origin': '*',
            },
            signal: controller.signal,
          }
        );

        if (active) {
          setData(response.data.data);
          setTotalCount(response.data.total);
          setError(null);
        }
      } catch (error) {
        if (!active) {
          return;
        }
        console.error('Error fetching data:', error);
        setError(getErrorMessage(error, t('imports.loadError')));
        setData([]);
        setTotalCount(0);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchTableData();
    return () => {
      active = false;
      controller.abort();
    };
  }, [currentPage, itemsPerPage, selectedSource, sortConfig, t, uploadedRange]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const fetchDateHighlights = async () => {
      setDateHighlightsLoading(true);
      try {
        const [uploadedFrom, uploadedTo] = dateRangeQuery(uploadedRange);
        const response = await apiClient.get<ImportFileHighlightsResponse>(
          API_GET_IMPORT_FILE_HIGHLIGHTS_ENDPOINT,
          {
            params: {
              uploaded_from: uploadedFrom,
              uploaded_to: uploadedTo,
              source: selectedSource || undefined,
            },
            signal: controller.signal,
          }
        );

        if (active) {
          setUploadedDateKeys(new Set(
            response.data.data.flatMap(({ uploaded_at }) =>
              uploaded_at ? [uploaded_at.slice(0, 10)] : []
            )
          ));
        }
      } catch (requestError) {
        if (active) {
          console.error('Error fetching import file dates:', requestError);
          setUploadedDateKeys(new Set());
        }
      } finally {
        if (active) {
          setDateHighlightsLoading(false);
        }
      }
    };

    fetchDateHighlights();
    return () => {
      active = false;
      controller.abort();
    };
  }, [selectedSource, uploadedRange]);

  const clearFilters = useCallback(() => {
    setUploadedRange([null, null]);
    setSelectedSource(null);
    setCurrentPage(1);
  }, []);

  const SortIcon = ({ column }: { column: SortableColumn }) => {
    if (sortConfig.column !== column) return null;
    return sortConfig.direction === 'asc' ? (
      <IconArrowUp size={14} style={{ marginLeft: 4 }} />
    ) : (
      <IconArrowDown size={14} style={{ marginLeft: 4 }} />
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${formatLocalizedNumber(bytes / Math.pow(k, i), language, {
      maximumFractionDigits: 2,
    })} ${sizes[i]}`;
  };

  const formatNumber = (value: number | null | undefined): string => {
    return value === null || value === undefined ? '-' : String(value);
  };

  const formatStatus = (status: string): string => {
    const statusLabels: Record<string, string> = {
      PROCESSED: t('imports.statusProcessed'),
      IMPORTED: t('imports.statusImported'),
      FAILED: t('imports.statusFailed'),
      UNKNOWN: t('imports.statusUnknown'),
    };

    return statusLabels[status] ?? status;
  };

  const formatSource = (source: ImportFile['source']): string => {
    const sourceLabels: Record<ImportFile['source'], string> = {
      desktop_app: t('imports.sourceDesktopApp'),
      web_app: t('imports.sourceWebApp'),
      direct_api: t('imports.sourceDirectApi'),
      sharepoint: t('imports.sourceSharePoint'),
      api_rerun: t('imports.sourceApiRerun'),
      legacy_unknown: t('imports.sourceLegacyUnknown'),
    };

    return sourceLabels[source] ?? source;
  };

  const renderSource = (importFile: ImportFile) => {
    const desktopVersion = importFile.qt_app_version?.trim();
    const versionLabel = desktopVersion
      ? t('imports.desktopAppVersion', { version: desktopVersion })
      : '';

    return (
      <Group gap={5} wrap="nowrap">
        <Text size="sm">{formatSource(importFile.source)}</Text>
        {importFile.source === 'desktop_app' && desktopVersion && (
          <Tooltip label={versionLabel} withArrow>
            <span
              className={classes.versionInfo}
              tabIndex={0}
              aria-label={versionLabel}
            >
              <IconInfoCircle size={15} stroke={1.8} aria-hidden="true" />
            </span>
          </Tooltip>
        )}
      </Group>
    );
  };

  const downloadFile = useCallback(async (importFile: ImportFile) => {
    try {
      const response = await apiClient({
        url: createApiGetImportFileEndpoint(importFile.id),
        method: 'GET',
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let fileName = importFile.file_name;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch?.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('File download failed:', error);
      setError(t('imports.downloadError'));
    }
  }, [t]);

  const handleProcess = useCallback((importFile: ImportFile) => {
    // Navigate to Add Well Test page with file_id in state
    navigate('/add_inference', { 
      state: { 
        importFileId: importFile.id,
        fileName: importFile.file_name 
      } 
    });
  }, [navigate]);

  const getUploadedDayProps = useCallback(
    (date: DateStringValue) => ({
      className: dateHighlightsLoading
        ? classes.reportDayLoading
        : uploadedDateKeys.has(date)
          ? classes.reportDay
          : undefined,
      title: dateHighlightsLoading
        ? t('results.loadingDates')
        : uploadedDateKeys.has(date)
          ? t('imports.filesUploaded')
          : undefined,
    }),
    [dateHighlightsLoading, t, uploadedDateKeys]
  );

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size="1rem" />} title={t('common.error')} color="red">
        {error}
      </Alert>
    );
  }

  return (
    <div className={classes.resultsSurface}>
      <PageHeader
        title={t('imports.title')}
        meta={t('imports.count', { count: totalCount })}
      />

      <Modal
        opened={diagnosticFile !== null}
        onClose={() => setDiagnosticFile(null)}
        title={t('imports.diagnosticsTitle')}
        size="lg"
      >
        {diagnosticFile && (
          <Stack gap="sm">
            <Text fw={700}>{diagnosticFile.file_name}</Text>
            <Text size="sm" c="dimmed">
              {formatSource(diagnosticFile.source)} ·{' '}
              {formatDisplayDateTime(diagnosticFile.uploaded_at)}
            </Text>
            <Text component="pre" className={classes.diagnosticText}>
              {diagnosticFile.error_message}
            </Text>
          </Stack>
        )}
      </Modal>

      <div className={classes.filterControls}>
        <DatePickerInput
          type="range"
          label={t('imports.uploadedAt')}
          placeholder={t('imports.uploadedPlaceholder')}
          value={uploadedRange}
          valueFormat={DATE_PICKER_VALUE_FORMAT}
          onChange={(value) => {
            setUploadedRange(value);
            setCurrentPage(1);
          }}
          allowSingleDateInRange
          clearable
          leftSection={<IconCalendar size="16" />}
          leftSectionPointerEvents="none"
          maxDate={new Date()}
          size="sm"
          getDayProps={getUploadedDayProps}
        />
        <Select
          label={t('imports.source')}
          placeholder={t('imports.allSources')}
          data={sourceOptions}
          value={selectedSource}
          onChange={(value) => {
            setSelectedSource(value as ImportFile['source'] | null);
            setCurrentPage(1);
          }}
          searchable
          clearable
          comboboxProps={{ withinPortal: false }}
        />
        <Button
          variant="subtle"
          color="green"
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className={classes.clearFiltersButton}
          h={36}
        >
          {t('results.clearFilters')}
        </Button>
      </div>

      <ScrollArea className={classes.tableContainer}>
        <Table
          className={classes.importTable}
          striped
          highlightOnHover
          withTableBorder
          stickyHeader
          stickyHeaderOffset={-1}
          aria-busy={loading}
          style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th
                onClick={() => handleSort('id')}
                classNames={{ th: classes.sortedHeader }}
              >
                <Text span fw={700}>ID</Text>
                <SortIcon column="id" />
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('uploaded_at')}
                classNames={{ th: classes.sortedHeader }}
              >
                <Text span fw={700}>{t('imports.uploadedAt')}</Text>
                <SortIcon column="uploaded_at" />
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('file_name')}
                classNames={{ th: classes.sortedHeader }}
              >
                <Text span fw={700}>{t('imports.fileName')}</Text>
                <SortIcon column="file_name" />
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('source')}
                classNames={{ th: classes.sortedHeader }}
              >
                <Text span fw={700}>{t('imports.source')}</Text>
                <SortIcon column="source" />
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('status')}
                classNames={{ th: classes.sortedHeader }}
              >
                <Text span fw={700}>{t('imports.status')}</Text>
                <SortIcon column="status" />
              </Table.Th>
              <Table.Th
                onClick={() => handleSort('total_records_count')}
                classNames={{ th: classes.sortedHeader }}
              >
                <Stack gap={0}>
                  <Group gap={4} wrap="nowrap">
                    <Text span fw={700}>{t('imports.records')}</Text>
                    <SortIcon column="total_records_count" />
                  </Group>
                  <Group gap={3} wrap="nowrap" className={classes.recordsLegend}>
                    <Text span>{t('imports.total')}</Text>
                    <Text span>(</Text>
                    <Text span className={classes.processedMetric}>{t('imports.processed')}</Text>
                    <Text span>/</Text>
                    <Text span className={classes.failedMetric}>{t('common.failed')}</Text>
                    <Text span>/</Text>
                    <Text span>{t('imports.duplicates')}</Text>
                    <Text span>)</Text>
                  </Group>
                </Stack>
              </Table.Th>
              <Table.Th classNames={{ th: classes.sortedHeader }}>
                <Text span fw={700}>{t('common.actions')}</Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && data.length === 0 ? (
              <TableSkeletonRows columns={7} />
            ) : data.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                  <Text c="dimmed">{t('imports.empty')}</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              data.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>
                    <Text size="sm">{item.id}</Text>
                  </Table.Td>
                  <Table.Td className={classes.uploadedCell}>
                    <Text size="sm" className={classes.timestamp}>
                      {formatDisplayDateTime(item.uploaded_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td className={classes.fileCell}>
                    <Stack gap={1}>
                      <Text size="sm" className={classes.fileName}>{item.file_name}</Text>
                      <Text size="xs" c="dimmed">{formatFileSize(item.size_bytes)}</Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    {renderSource(item)}
                  </Table.Td>
                  <Table.Td>
                    <Text
                      size="sm"
                      fw={700}
                      c={
                        item.status === 'PROCESSED'
                          ? 'green'
                          : item.status === 'IMPORTED'
                          ? 'yellow'
                          : item.status === 'FAILED'
                          ? 'red'
                          : item.status === 'UNKNOWN'
                          ? 'dark'
                          : 'dimmed'
                      }
                    >
                      {formatStatus(item.status)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {[
                      item.total_records_count,
                      item.processed_records_count,
                      item.failed_records_count,
                      item.duplicates_count,
                    ].every((value) => value === null || value === undefined) ? (
                      <Text size="sm" c="dimmed">-</Text>
                    ) : (
                      <Group gap={4} wrap="nowrap" className={classes.recordSummary}>
                        <Tooltip label={t('imports.totalRecords')} withArrow>
                          <Text span>{formatNumber(item.total_records_count)}</Text>
                        </Tooltip>
                        <Text span c="dimmed">(</Text>
                        <Tooltip label={t('imports.processed')} withArrow>
                          <Text span className={classes.processedMetric}>
                            {formatNumber(item.processed_records_count)}
                          </Text>
                        </Tooltip>
                        <Text span c="dimmed">/</Text>
                        <Tooltip label={t('common.failed')} withArrow>
                          <Text
                            span
                            className={item.failed_records_count ? classes.failedMetric : classes.emptyFailedMetric}
                          >
                            {formatNumber(item.failed_records_count)}
                          </Text>
                        </Tooltip>
                        <Text span c="dimmed">/</Text>
                        <Tooltip label={t('imports.duplicates')} withArrow>
                          <Text span>{formatNumber(item.duplicates_count)}</Text>
                        </Tooltip>
                        <Text span c="dimmed">)</Text>
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {item.status !== 'PROCESSED' && (
                        <Tooltip label={t('imports.process')} withArrow>
                          <ActionIcon
                            variant="outline"
                            color="orange"
                            size="lg"
                            className={classes.rowAction}
                            aria-label={t('imports.process')}
                            onClick={() => handleProcess(item)}
                          >
                            <IconRefresh size={17} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label={t('imports.download')} withArrow>
                        <ActionIcon
                          variant="outline"
                          color="green"
                          size="lg"
                          className={classes.rowAction}
                          aria-label={t('imports.download')}
                          onClick={() => downloadFile(item)}
                        >
                          <IconDownload size={17} />
                        </ActionIcon>
                      </Tooltip>
                      {item.error_message && (
                        <Tooltip
                          label={
                            item.status === 'FAILED'
                              ? t('imports.viewError')
                              : t('imports.viewWarnings')
                          }
                          withArrow
                        >
                          <ActionIcon
                            variant="outline"
                            color={item.status === 'FAILED' ? 'red' : 'orange'}
                            size="lg"
                            className={classes.rowAction}
                            aria-label={
                              item.status === 'FAILED'
                                ? t('imports.viewError')
                                : t('imports.viewWarnings')
                            }
                            onClick={() => setDiagnosticFile(item)}
                          >
                            <IconAlertCircle size={17} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <div className={classes.paginationRow}>
        <Text size="sm" c="dimmed" className={classes.resultRange}>
          {t('imports.showing', {
            from: data.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1,
            to: Math.min(currentPage * itemsPerPage, totalCount),
            total: totalCount,
          })}
        </Text>
        <Group gap="xs" wrap="nowrap" className={classes.rowsControl}>
          <Text size="xs" fw={700}>{t('results.rows')}</Text>
          <NativeSelect
            aria-label={t('results.rowsPerPage')}
            data={PAGE_SIZE_OPTIONS}
            value={String(itemsPerPage)}
            onChange={(event) => {
              const value = event.currentTarget.value;
              if (value) {
                setCurrentPage(1);
                setItemsPerPage(Number(value));
              }
            }}
            className={classes.rowsSelect}
            size="xs"
          />
        </Group>
        <Pagination
          total={Math.max(1, Math.ceil(totalCount / itemsPerPage))}
          value={currentPage}
          onChange={setCurrentPage}
          className={classes.pagination}
        />
      </div>
    </div>
  );
}

